import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { getDisplayName, getPrincipalInitials } from '../../../../models/NameUtils';
import { useContactAutocomplete } from '../../../../queries/Contact/useContactAutocomplete';
import { useConversationRemoveContactMutation } from '../../../../queries/Conversation/useConversationRemoveContactMutation';
import { EditItemPenIcon } from '../../svgs/icons/EditItemPenIcon';
import { UserIconAvatar } from '../../UserIconAvatar';
import { styleSheet } from './styles';

function phoneDigitsMatch(phoneA: string, phoneB: string) {
	const digitsA = phoneA.replace(/\D/g, '');
	const digitsB = phoneB.replace(/\D/g, '');
	return digitsA.includes(digitsB) || digitsB.includes(digitsA);
}

function contactHasMatchingPhone(contact: Api.IContact, conversationPhone: string) {
	return contact.phoneNumbers?.some(p => {
		const parsed = p?.value?.match(/\d+/g)?.join('') ?? '';
		return parsed.length > 0 && phoneDigitsMatch(parsed, conversationPhone);
	});
}

interface ConversationContactEditorProps {
	conversation: Api.ConversationViewModel;
	onContactChanged?: () => void;
}

export function ConversationContactEditor({ conversation, onContactChanged }: ConversationContactEditorProps) {
	const [isEditing, setIsEditing] = React.useState(false);

	const recipient = conversation.toNumbers?.[0];
	const contact = recipient?.contact;
	const phoneNumber = recipient?.number?.standard || recipient?.number?.e164 || '';

	if (isEditing) {
		return (
			<ConversationContactSearch
				conversation={conversation}
				phoneNumber={phoneNumber}
				onCancel={() => setIsEditing(false)}
				onContactChanged={() => {
					setIsEditing(false);
					onContactChanged?.();
				}}
			/>
		);
	}

	if (contact) {
		const displayName = getDisplayName(contact) || phoneNumber;
		const initials = getPrincipalInitials(contact) || '?';
		return (
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.avatar)} style={{ background: '#4A90D9' }}>
					{initials}
				</div>
				<div>
					<div className={css(styleSheet.nameRow)}>
						<span className={css(styleSheet.contactName)}>{displayName}</span>
						<button className={css(styleSheet.editButton)} onClick={() => setIsEditing(true)} title='Change contact'>
							<EditItemPenIcon fillColor='currentColor' />
						</button>
					</div>
					<span className={css(styleSheet.phoneNumber)}>{phoneNumber}</span>
				</div>
			</div>
		);
	}

	// Unassociated mode
	return (
		<div className={css(styleSheet.container)}>
			<UserIconAvatar styleDeclaration={styleSheet.avatar} />
			<div>
				<span className={css(styleSheet.contactName)}>{phoneNumber}</span>
				<div>
					<button className={css(styleSheet.addContactLink)} onClick={() => setIsEditing(true)}>
						+ Add contact
					</button>
				</div>
			</div>
		</div>
	);
}

function ConversationContactSearch({
	conversation,
	phoneNumber,
	onCancel,
	onContactChanged,
}: {
	conversation: Api.ConversationViewModel;
	phoneNumber: string;
	onCancel: () => void;
	onContactChanged: () => void;
}) {
	const [searchText, setSearchText] = React.useState('');
	const [isSelectSaving, setIsSelectSaving] = React.useState(false);
	const debouncedSearch = useDebounceValue(searchText, 300);
	const containerRef = React.useRef<HTMLDivElement>(null);

	const removeContactMutation = useConversationRemoveContactMutation({
		onSuccess: onContactChanged,
		onError: () => toast.error('Failed to remove contact association. Please try again.'),
	});

	const isSaving = isSelectSaving || removeContactMutation.isPending;
	const isSavingRef = React.useRef(false);
	isSavingRef.current = isSaving;

	React.useEffect(() => {
		const handlePointerDown = (event: MouseEvent | TouchEvent) => {
			if (isSavingRef.current) {
				return;
			}
			if (containerRef.current?.contains(event.target as Node)) {
				return;
			}
			onCancel();
		};
		document.addEventListener('mousedown', handlePointerDown);
		document.addEventListener('touchstart', handlePointerDown);
		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
			document.removeEventListener('touchstart', handlePointerDown);
		};
	}, [onCancel]);

	const recipient = conversation.toNumbers?.[0];
	const hasContact = !!recipient?.contact;

	const { data, isFetching } = useContactAutocomplete({
		enabled: debouncedSearch.length >= 2,
		fragment: debouncedSearch,
		pageSize: 10,
		searchFields: 'nameParts',
	});

	const contacts = (data?.pages?.flatMap(page => page?.values ?? []) ?? [])
		.filter((c): c is Api.IContact => c != null)
		.filter(c => contactHasMatchingPhone(c, phoneNumber));

	const handleSelectContact = async (contact: Api.IContact) => {
		setIsSelectSaving(true);
		try {
			const matchingPhone = contact.phoneNumbers?.find(p => {
				const parsed = p?.value?.match(/\d+/g)?.join('') ?? '';
				return parsed.length > 0 && phoneDigitsMatch(parsed, phoneNumber);
			});
			const textContact: Api.ITextContact = {
				...contact,
				selectedPhoneNumber: matchingPhone ?? { value: phoneNumber },
			};
			await conversation.updateRecipients(textContact);
			onContactChanged();
		} catch {
			toast.error('Failed to associate contact. Please try again.');
		} finally {
			setIsSelectSaving(false);
		}
	};

	const showResults = debouncedSearch.length >= 2;

	return (
		<div ref={containerRef} className={css(styleSheet.editContainer, isSaving && styleSheet.saving)}>
			<div className={css(styleSheet.editHeader)}>
				<span className={css(styleSheet.editLabel)}>Change Contact</span>
				<button className={css(styleSheet.closeButton)} onClick={onCancel}>
					&times;
				</button>
			</div>
			<input
				autoFocus
				className={css(styleSheet.searchInput)}
				onChange={e => setSearchText(e.target.value)}
				placeholder='Search contacts...'
				type='text'
				value={searchText}
			/>
			{showResults && (
				<div className={css(styleSheet.resultsList)}>
					{isFetching ? (
						<div className={css(styleSheet.noResults)}>Searching...</div>
					) : contacts.length === 0 ? (
						<div className={css(styleSheet.noResults)}>No contacts found with this phone number</div>
					) : (
						contacts.map(contact => {
							const name = getDisplayName(contact) || '(No name)';
							const initials = getPrincipalInitials(contact) || '?';
							const email = contact.emailAddresses?.[0]?.value ?? '';
							return (
								<button
									className={css(styleSheet.resultItem)}
									key={contact.id}
									onClick={() => handleSelectContact(contact)}
									type='button'
								>
									<div className={css(styleSheet.resultAvatar)} style={{ background: '#4A90D9' }}>
										{initials}
									</div>
									<div>
										<div className={css(styleSheet.resultName)}>{name}</div>
										{email ? <div className={css(styleSheet.resultEmail)}>{email}</div> : null}
									</div>
								</button>
							);
						})
					)}
				</div>
			)}
			{hasContact ? (
				<button
					className={css(styleSheet.removeLink)}
					onClick={() =>
						removeContactMutation.mutate({
							conversationId: conversation.id,
							toNumbers: conversation.toNumbers,
						})
					}
					type='button'
					disabled={removeContactMutation.isPending}
				>
					Remove contact association
				</button>
			) : null}
		</div>
	);
}
